/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as size from '@constant/size';
import * as color from '@constant/color';
import * as text from '@constant/text';
import TextBlock from 'component/common/block/text-block';
import CheckBoxInput from '@component/common/input/checkbox-input';
import Gap from '@component/common/gap';
import Scroll from '@component/common/scroll';
import { useTerm } from '@hook/auth/useAuth';

const CheckBoxesContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: ${size.MARGIN_1};
`;

const CheckBoxContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const TermDescription = styled.div`
  white-space: pre-wrap;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.GRAY_4};
`;

const CONSENT_GAP = '5px';
const TERM_SCROLL_HEIGHT = '100px';

const TERM_VIEW_TEXT_DESIGN = {
  text: { size: 'tiny', color: color.GRAY_1 },
} as const;

const Wrapper = styled.div`
  width: 100%;
`;

const Term: React.FC<any> = () => {
  const { totalTerm, adultTerm, serviceTerm, informationTerm, marketingTerm } = useTerm();

  return (
    <Wrapper>
      <TextBlock
        text='약관 동의'
        design={{ text: { size: 'medium', bold: true }, box: { width: '100%', alignX: 'left' } }}
      />
      <Gap height='10px' />
      <CheckBoxesContainer>
        <CheckBoxContainer>
          <CheckBoxInput
            text='전체 동의'
            checked={totalTerm.checked}
            design={{ checkSize: 'small', fontSize: 'small', gap: CONSENT_GAP }}
            onChange={totalTerm.handler}
          />
        </CheckBoxContainer>
        <CheckBoxContainer>
          <CheckBoxInput
            text='만14세이상가입 동의 (필수)'
            checked={adultTerm.checked}
            design={{ checkSize: 'small', fontSize: 'small', gap: CONSENT_GAP }}
            onChange={adultTerm.handler}
          />
        </CheckBoxContainer>
        <CheckBoxContainer>
          <CheckBoxInput
            text='서비스이용 동의 (필수)'
            checked={serviceTerm.checked}
            design={{ checkSize: 'small', fontSize: 'small', gap: CONSENT_GAP }}
            onChange={serviceTerm.handler}
          />
          <TextBlock
            text='약관보기'
            design={{
              text: { size: 'tiny', color: color.GRAY_1, underline: true },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: serviceTerm.descriptionHandler }}
          />
        </CheckBoxContainer>
        {serviceTerm.descriptionChecked && (
          <TermDescription>
            <Scroll direction='y' height={TERM_SCROLL_HEIGHT}>
              <TextBlock text={text.SERVICE_TERM} design={TERM_VIEW_TEXT_DESIGN} />
            </Scroll>
          </TermDescription>
        )}
        <CheckBoxContainer>
          <CheckBoxInput
            text='개인정보처리방침 동의 (필수)'
            checked={informationTerm.checked}
            design={{ checkSize: 'small', fontSize: 'small', gap: CONSENT_GAP }}
            onChange={informationTerm.handler}
          />
          <TextBlock
            text='약관보기'
            design={{ text: { size: 'tiny', color: color.GRAY_1, underline: true } }}
            click={{ handler: informationTerm.descriptionHandler }}
          />
        </CheckBoxContainer>
        {informationTerm.descriptionChecked && (
          <TermDescription>
            <Scroll direction='y' height={TERM_SCROLL_HEIGHT}>
              <TextBlock text={text.INFORMATION_TERM} design={TERM_VIEW_TEXT_DESIGN} />
            </Scroll>
          </TermDescription>
        )}
        <CheckBoxContainer>
          <CheckBoxInput
            text='마케팅정보수신 동의 (선택)'
            checked={marketingTerm.checked}
            design={{ checkSize: 'small', fontSize: 'small', gap: CONSENT_GAP }}
            onChange={marketingTerm.handler}
          />
          <TextBlock
            text='약관보기'
            design={{ text: { size: 'tiny', color: color.GRAY_1, underline: true } }}
            click={{ handler: marketingTerm.descriptionHandler }}
          />
        </CheckBoxContainer>
        {marketingTerm.descriptionChecked && (
          <TermDescription>
            <Scroll direction='y' height={TERM_SCROLL_HEIGHT}>
              <TextBlock text={text.MARKETING_TERM} design={TERM_VIEW_TEXT_DESIGN} />
            </Scroll>
          </TermDescription>
        )}
      </CheckBoxesContainer>
    </Wrapper>
  );
};

export default Term;
